import type {
  DomainBlock,
  DomainDocument,
  JsonValue,
} from "@note-app/contracts";
import { parseColumnsData } from "./blocknote-custom-blocks/columns-model";

export const EDITOR_DOCUMENT_SCHEMA_VERSION = 1;

export type AdapterIssueCode =
  | "invalid-document"
  | "invalid-schema-version"
  | "invalid-block"
  | "duplicate-block-id"
  | "generated-block-id"
  | "unsupported-block-preserved"
  | "unsupported-props-preserved"
  | "legacy-columns-migrated"
  | "invalid-unsupported-payload";

export interface AdapterIssue {
  code: AdapterIssueCode;
  path: string;
  message: string;
}

export interface AdapterRecovery {
  document: DomainDocument;
  requiresConfirmation: true;
  dataLossPossible: true;
}

export class BlockNoteAdapterError extends Error {
  readonly code: AdapterIssueCode;
  readonly issues: readonly AdapterIssue[];
  readonly recovery: AdapterRecovery;
  readonly cause?: unknown;

  constructor(
    message: string,
    code: AdapterIssueCode,
    issues: readonly AdapterIssue[],
    recovery: AdapterRecovery,
    cause?: unknown,
  ) {
    super(message);
    this.name = "BlockNoteAdapterError";
    this.code = code;
    this.issues = issues;
    this.recovery = recovery;
    if (cause !== undefined) {
      this.cause = cause;
    }
  }
}

export type AdapterResult<T> =
  | { ok: true; value: T; warnings: readonly AdapterIssue[] }
  | { ok: false; error: BlockNoteAdapterError };

export function createEmptyDocument(
  schemaVersion = EDITOR_DOCUMENT_SCHEMA_VERSION,
): DomainDocument {
  return { schemaVersion, blocks: [] };
}

function createRecovery(): AdapterRecovery {
  return {
    document: createEmptyDocument(),
    requiresConfirmation: true,
    dataLossPossible: true,
  };
}

export function adapterFailure(
  message: string,
  code: AdapterIssueCode,
  issues: readonly AdapterIssue[],
  cause?: unknown,
): AdapterResult<never> {
  return {
    ok: false,
    error: new BlockNoteAdapterError(
      message,
      code,
      issues,
      createRecovery(),
      cause,
    ),
  };
}

export function isPlainRecord(
  value: unknown,
): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function isJsonValue(value: unknown): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  return isPlainRecord(value) && Object.values(value).every(isJsonValue);
}

function validateBlock(
  value: unknown,
  path: string,
  ids: Set<string>,
  issues: AdapterIssue[],
): value is DomainBlock {
  if (!isPlainRecord(value)) {
    issues.push({
      code: "invalid-block",
      path,
      message: "Block must be an object",
    });
    return false;
  }

  let valid = true;
  if (typeof value.id !== "string" || value.id.length === 0) {
    issues.push({
      code: "invalid-block",
      path: `${path}.id`,
      message: "Block ID must be a non-empty string",
    });
    valid = false;
  } else if (ids.has(value.id)) {
    issues.push({
      code: "duplicate-block-id",
      path: `${path}.id`,
      message: `Duplicate block ID: ${value.id}`,
    });
    valid = false;
  } else {
    ids.add(value.id);
  }

  if (typeof value.type !== "string" || value.type.length === 0) {
    issues.push({
      code: "invalid-block",
      path: `${path}.type`,
      message: "Block type must be a non-empty string",
    });
    valid = false;
  }
  if (!isPlainRecord(value.props) || !isJsonValue(value.props)) {
    issues.push({
      code: "invalid-block",
      path: `${path}.props`,
      message: "Block props must contain JSON values only",
    });
    valid = false;
  }
  if (!isJsonValue(value.content)) {
    issues.push({
      code: "invalid-block",
      path: `${path}.content`,
      message: "Block content must be a JSON value",
    });
    valid = false;
  }
  if (!Array.isArray(value.children)) {
    issues.push({
      code: "invalid-block",
      path: `${path}.children`,
      message: "Block children must be an array",
    });
    return false;
  }

  for (const [index, child] of value.children.entries()) {
    if (!validateBlock(child, `${path}.children[${index}]`, ids, issues)) {
      valid = false;
    }
  }
  if (
    value.type === "columns" &&
    isPlainRecord(value.props) &&
    typeof value.props.columnsData === "string"
  ) {
    const columns = parseColumnsData(value.props.columnsData);
    if (columns.ok) {
      for (const [columnIndex, column] of columns.value.columns.entries()) {
        for (const [blockIndex, block] of column.blocks.entries()) {
          if (
            !validateBlock(
              block,
              `${path}.props.columnsData.columns[${columnIndex}].blocks[${blockIndex}]`,
              ids,
              issues,
            )
          ) {
            valid = false;
          }
        }
      }
    }
  }
  return valid;
}

export function validateDomainDocument(
  input: unknown,
): AdapterResult<DomainDocument> {
  const issues: AdapterIssue[] = [];
  if (!isPlainRecord(input)) {
    return adapterFailure("Document must be an object", "invalid-document", [
      {
        code: "invalid-document",
        path: "$",
        message: "Document must be an object",
      },
    ]);
  }

  if (
    typeof input.schemaVersion !== "number" ||
    !Number.isInteger(input.schemaVersion) ||
    input.schemaVersion < 1
  ) {
    issues.push({
      code: "invalid-schema-version",
      path: "$.schemaVersion",
      message: "schemaVersion must be a positive integer",
    });
  }
  if (!Array.isArray(input.blocks)) {
    issues.push({
      code: "invalid-document",
      path: "$.blocks",
      message: "blocks must be an array",
    });
  } else {
    const ids = new Set<string>();
    for (const [index, block] of input.blocks.entries()) {
      validateBlock(block, `$.blocks[${index}]`, ids, issues);
    }
  }

  if (issues.length > 0) {
    return adapterFailure(
      "Document validation failed",
      issues[0]?.code ?? "invalid-document",
      issues,
    );
  }

  return {
    ok: true,
    value: input as unknown as DomainDocument,
    warnings: [],
  };
}
